import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Context} from '../contexts/WordList';
import {fetchStudyStats} from '../helpers/localApi';

const DAY_MS = 86400000;

function dayLabel(dateStr) {
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString(undefined, {weekday: 'short'});
}

function shortDate(dateStr) {
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
}

function retentionClass(retention) {
  if (retention >= 85) return 'heat-good';
  if (retention >= 65) return 'heat-mid';
  if (retention > 0) return 'heat-bad';
  return 'heat-none';
}

export const ProgressPage = () => {
  const [state] = useContext(Context);
  const {language} = state;
  const langName = language === 'hebrew' ? 'Hebrew' : 'Farsi';

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchStudyStats({language, days: 30})
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load stats.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  // Last 14 days of activity (fill gaps so the chart is stable).
  const activity = useMemo(() => {
    const byDate = {};
    ((stats && stats.daily) || []).forEach((d) => {
      byDate[d.date] = d;
    });
    const days = [];
    const now = new Date();
    for (let i = 13; i >= 0; i--) {
      const date = new Date(now.getTime() - i * DAY_MS);
      const key = date.toISOString().slice(0, 10);
      days.push({
        date: key,
        reviews: byDate[key] ? byDate[key].reviews : 0,
        correct: byDate[key] ? byDate[key].correct : 0,
      });
    }
    return days;
  }, [stats]);

  const forecast = useMemo(() => {
    const byDate = {};
    ((stats && stats.forecast) || []).forEach((f) => {
      byDate[f.date] = f.due;
    });
    const days = [];
    const now = new Date();
    let dueNow = 0;
    for (let i = 0; i < 7; i++) {
      const date = new Date(now.getTime() + i * DAY_MS);
      const key = date.toISOString().slice(0, 10);
      const due = byDate[key] || 0;
      if (i === 0) dueNow = due;
      days.push({date: key, due});
    }
    return {days, dueNow};
  }, [stats]);

  const maxReviews = Math.max(1, ...activity.map((d) => d.reviews));
  const maxDue = Math.max(1, ...forecast.days.map((d) => d.due));

  if (loading) {
    return (
      <div className="progress-page">
        <div className="progress-loading">Loading progress…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="progress-page">
        <div className="progress-loading">{error}</div>
      </div>
    );
  }

  const totals = (stats && stats.totals) || {mastered: 0, learning: 0, new: 0};

  return (
    <div className="progress-page">
      <div className="progress-header">
        <h2 className="progress-title">Progress</h2>
        <p className="progress-subtitle">
          Your {langName} learning stats from the last 30 days
        </p>
      </div>

      <div className="progress-kpi-row">
        <div className="progress-kpi">
          <span className="progress-kpi-value">{stats.streak}</span>
          <span className="progress-kpi-label">Day streak</span>
        </div>
        <div className="progress-kpi progress-kpi-accent">
          <span className="progress-kpi-value">{forecast.dueNow}</span>
          <span className="progress-kpi-label">Due now</span>
        </div>
        <div className="progress-kpi">
          <span className="progress-kpi-value">{stats.retentionRate}%</span>
          <span className="progress-kpi-label">Retention</span>
        </div>
      </div>

      <div className="progress-kpi-row">
        <div className="progress-kpi progress-kpi-mastered">
          <span className="progress-kpi-value">{totals.mastered}</span>
          <span className="progress-kpi-label">Mastered</span>
        </div>
        <div className="progress-kpi progress-kpi-learning">
          <span className="progress-kpi-value">{totals.learning}</span>
          <span className="progress-kpi-label">Learning</span>
        </div>
        <div className="progress-kpi progress-kpi-new">
          <span className="progress-kpi-value">{totals.new}</span>
          <span className="progress-kpi-label">New</span>
        </div>
      </div>

      <section className="progress-card">
        <h3 className="progress-card-title">Activity — last 14 days</h3>
        <div className="progress-bars">
          {activity.map((d) => (
            <div key={d.date} className="progress-bar-col" title={`${shortDate(d.date)}: ${d.reviews} reviews`}>
              <div className="progress-bar-track">
                <div
                  className="progress-bar-fill"
                  style={{height: `${Math.round((d.reviews / maxReviews) * 100)}%`}}
                  data-level={d.reviews > 0 ? 'some' : 'none'}
                />
              </div>
              <span className="progress-bar-label">{dayLabel(d.date).slice(0, 2)}</span>
            </div>
          ))}
        </div>
        <p className="progress-card-meta">
          {stats.totalReviews} reviews · {stats.reviewsToday} today
        </p>
      </section>

      <section className="progress-card">
        <h3 className="progress-card-title">Reviews due — next 7 days</h3>
        <div className="progress-bars">
          {forecast.days.map((d) => (
            <div key={d.date} className="progress-bar-col" title={`${shortDate(d.date)}: ${d.due} due`}>
              <div className="progress-bar-track">
                <div
                  className="progress-bar-fill progress-bar-fill-forecast"
                  style={{height: `${Math.round((d.due / maxDue) * 100)}%`}}
                  data-level={d.due > 0 ? 'some' : 'none'}
                />
              </div>
              <span className="progress-bar-label">{dayLabel(d.date).slice(0, 2)}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="progress-card">
        <h3 className="progress-card-title">Retention by category</h3>
        {(stats.byTag || []).length === 0 ? (
          <p className="progress-card-meta">Review some words to see per-category retention.</p>
        ) : (
          <div className="progress-heat-grid">
            {stats.byTag.map((t) => (
              <div key={t.tag} className={`progress-heat-chip ${retentionClass(t.retention)}`}>
                <span className="progress-heat-tag">{t.tag}</span>
                <span className="progress-heat-value">{t.retention}%</span>
                <span className="progress-heat-meta">{t.reviews} reviews</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
